import { useState } from 'react';
import { ArrowRight, Briefcase, Building2, Lock, ShieldCheck, Store, User } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { AuthThemeToggle } from '../components/AuthThemeToggle';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Logo } from '../components/Logo';
import { cn } from '../lib/cn';
import { useBrand } from '../brand/BrandProvider';
import { login, destinationForRole } from '../api/auth';
import { USE_MOCKS } from '../api/client';

type DemoRole = {
  key: string;
  name: string;
  email: string;
  roleLabel: string;
  destination: '/' | '/admin' | '/reseller' | '/staff';
  context: string;
  icon: React.ReactNode;
};

const DEMO_ROLES: DemoRole[] = [
  {
    key: 'owner',
    name: 'Imran Khan',
    email: 'imran@khan-bakery.com',
    roleLabel: 'Business owner',
    destination: '/',
    context: 'Owns Khan Bakery + 2 more businesses',
    icon: <Briefcase className="w-4 h-4" />,
  },
  {
    key: 'admin',
    name: 'ITCity Admin',
    email: 'admin@itcitysoft.com',
    roleLabel: 'Platform admin',
    destination: '/admin',
    context: 'ITCity staff · all 521 businesses',
    icon: <ShieldCheck className="w-4 h-4" />,
  },
  {
    key: 'reseller',
    name: 'Rakib Ahmed',
    email: 'rakib@acme-resellers.com',
    roleLabel: 'Reseller admin',
    destination: '/reseller',
    context: 'Acme Resellers · 128 customers',
    icon: <Building2 className="w-4 h-4" />,
  },
  {
    key: 'staff',
    name: 'Rina Akter',
    email: 'cashier@khan-bakery.com',
    roleLabel: 'Business staff',
    destination: '/staff',
    context: 'Cashier at Khan Bakery (POS-only)',
    icon: <Store className="w-4 h-4" />,
  },
];

/** All seeded demo accounts share this dev password. */
const DEMO_PASSWORD = 'demo1234';

/**
 * Bare-shell login page. Two-column on desktop:
 *  - Left:  brand pitch + Mercury-style soft glow background
 *  - Right: form card
 * On mobile, the left collapses; only the form shows.
 */
export function Login() {
  const brand = useBrand();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { user } = await login(email.trim(), password);
      navigate({ to: destinationForRole(user.role) });
    } catch {
      setError('That email and password don’t match. Please try again.');
      setSubmitting(false);
    }
  }

  /** Demo quick-login: actually authenticate (all demo accounts share demo1234), then route. */
  async function demoLogin(r: DemoRole) {
    setSubmitting(true);
    setError(null);
    try {
      const { user } = await login(r.email, DEMO_PASSWORD);
      navigate({ to: destinationForRole(user.role) });
    } catch {
      setError(`Demo sign-in for ${r.email} failed — is it seeded?`);
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid lg:grid-cols-2">
      {/* Left — pitch */}
      <div className="hidden lg:flex relative overflow-hidden p-12 xl:p-20 flex-col justify-between border-r border-zinc-200 dark:border-zinc-700">
        <div className="glow-bg absolute inset-0 pointer-events-none" aria-hidden="true" />

        <div className="relative">
          <div className="flex items-center gap-2.5">
            <Logo />
            <span className="font-semibold text-lg tracking-tight text-zinc-950 dark:text-zinc-50">
              {brand.name}
            </span>
          </div>
        </div>

        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-lg">
            Cloud POS,
            <br />
            <span className="text-zinc-500 dark:text-zinc-500">built for Bangladesh.</span>
          </h1>
          <p className="mt-6 text-base text-zinc-600 dark:text-zinc-300 max-w-md">
            Sales, inventory, payroll, repair, restaurant — all on a single calm interface.
            {brand.isPlatform
              ? ' Sold by ITCity directly and by a network of resellers across the country.'
              : ` From ${brand.name}, built for businesses across the country.`}
          </p>
          <ul className="mt-8 space-y-3 text-sm text-zinc-700 dark:text-zinc-200">
            <Bullet>BDT-native; SSLCommerz built in</Bullet>
            <Bullet>Multi-business, multi-location</Bullet>
            <Bullet>Bring your own database, or use ours</Bullet>
            <Bullet>Light + dark, mobile + desktop, en + bn</Bullet>
          </ul>
        </div>

        <div className="relative text-xs text-zinc-500 dark:text-zinc-400">
          {brand.isPlatform ? '© 2026 ITCity Ltd. · Sylhet, Bangladesh' : `© 2026 ${brand.name}`}
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex flex-col justify-center p-6 sm:p-12">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
          <AuthThemeToggle />
        </div>
        <div className="lg:hidden flex items-center gap-2.5 mb-12">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </div>

        <div className="w-full max-w-sm mx-auto">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Welcome back
          </h2>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Sign in to your {brand.name} account.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            {error && (
              <div
                role="alert"
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
              >
                {error}
              </div>
            )}
            <Input
              label="Email"
              type="email"
              required
              placeholder="imran@khan-bakery.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <div>
              <Input
                label="Password"
                type="password"
                required
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="flex items-center justify-between mt-2">
                <label className="inline-flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                  <input
                    type="checkbox"
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Stay signed in for 7 days
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-brand dark:text-brand-dm hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={submitting}
              iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <div className="flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400">
              <Lock className="w-3 h-3" />
              <span>Encrypted · 2-factor available</span>
            </div>
          </form>

          <p className="mt-10 text-sm text-zinc-600 dark:text-zinc-300 text-center">
            Don't have an account?{' '}
            <Link to="/signup" className="font-medium text-brand dark:text-brand-dm hover:underline">
              Start a free trial
            </Link>
          </p>

          {/* Demo quick-login + route directory — DEV/mock only; never rendered in production. */}
          {USE_MOCKS && (
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
                Demo
              </span>
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                Sign in as any role to explore
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DEMO_ROLES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  disabled={submitting}
                  onClick={() => demoLogin(r)}
                  className={cn(
                    'group flex items-start gap-3 p-3 rounded-lg text-left transition',
                    'bg-white dark:bg-zinc-900',
                    'border border-zinc-200 dark:border-zinc-700',
                    'hover:border-zinc-300 dark:hover:border-zinc-600',
                    'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
                  )}
                >
                  <span className="w-8 h-8 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
                    {r.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">
                        {r.name}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">
                        {r.roleLabel}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">
                      {r.context}
                    </p>
                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mono truncate mt-0.5">
                      {r.email} → {r.destination}
                    </p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-200 flex-shrink-0 mt-1" />
                </button>
              ))}
            </div>

            <details className="mt-4 group">
              <summary className="cursor-pointer text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 inline-flex items-center gap-1.5">
                <span className="group-open:rotate-90 transition-transform inline-block">▸</span>
                Other entry points
              </summary>
              <div className="mt-3 grid grid-cols-2 gap-1.5 text-xs">
                <DemoLink to="/admin/login" label="Admin login (separate UI)" />
                <DemoLink to="/reseller/login" label="Reseller-branded login" />
                <DemoLink to="/signup" label="Signup wizard" />
                <DemoLink to="/forgot-password" label="Forgot password" />
                <DemoLink to="/reset-password" label="Reset password" />
                <DemoLink to="/mfa-setup" label="MFA setup" />
                <DemoLink to="/accept-invite" label="Accept invitation" />
                <DemoLink to="/reseller/set-password" label="Reseller invite landing" />
                <DemoLink to="/reseller/onboarding" label="Reseller onboarding" />
                <DemoLink to="/bootstrap" label="First-install setup" />
                <DemoLink to="/welcome" label="First-login welcome" />
                <DemoLink to="/suspended" label="Subscription suspended" />
                <DemoLink to="/pricing" label="Pricing page" />
                <DemoLink to="/landing" label="Marketing landing" />
                <DemoLink to="/pos/sso-expired" label="POS · SSO expired" />
                <DemoLink to="/pos/suspended" label="POS · suspended" />
                <DemoLink to="/pos/db-unreachable" label="POS · DB unreachable" />
                <DemoLink to="/errors/404" label="Error · 404" />
                <DemoLink to="/errors/403" label="Error · 403" />
                <DemoLink to="/errors/500" label="Error · 500" />
              </div>
            </details>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DemoLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to as never}
      className="px-2 py-1.5 rounded text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-950 dark:hover:text-zinc-50 transition truncate"
    >
      {label}
    </Link>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <ArrowRight className="w-4 h-4 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}
